import { ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DefaultSourcingService } from './default-sourcing.service';
import { JsonObjectFactoryService } from '../../../shared/_services/json-object-factory.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import * as _ from 'lodash-es';
import { forkJoin, Observable, Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
@Component({
    selector: 'gtw-default-sourcing',
    templateUrl: './default-sourcing.component.html',
    styleUrls: ['./default-sourcing.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class DefaultSourcingComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();
  private readonly subs = new Subscription();

  // Standard component properties
  currentTabName = "Form Entry";
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  showFormLoader = true;

  // Component data properties
  rowData: any = {};
  filterParams: any = {};
  globalService: any = {};
  USER_SETTINGS: any = {};
  GENERAL_CONFIG: any = {};
  SERVER_CONFIG: any = {};

  // Modal properties
  modalTitle = "Default Sourcing Override";

  // Form data
  overrideEntity: any = {
    TAX_YEAR: '',
    HO_LEID_RP: '',
    BASKET: '',
    FSI_KEY: ''
  };
  overrideEntity_backup: any = {};

  // Dropdown options
  ho_leid_RP_options: any[] = [];
  basket_options: any[] = [];

  // Edit mode flag
  isEdit = false;

  // User settings
  logged_in_user: string = '';

  // Initialization flags
  private isInitialized = false;
  private isLoading = false;

  baseURLs: any = {
    admin: '',
    api: '',
    custom: '',
    efile: '',
    pdfEngine: '',
    bulkPdfEngine: ''
  };

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private gtwSnackbarService: GtwSnackbarService,
    private defaultSourcingService: DefaultSourcingService,
    private jsonObjectFactory: JsonObjectFactoryService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.tryInit();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = header.globalParams || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.SERVER_CONFIG = header.serverConfigData || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
  }

  /**
   * Gate function to prevent duplicate initialization
   */
  private tryInit(): void {
    if (this.isInitialized || this.isLoading) {
      return;
    }

    if (!this.globalService || !this.GENERAL_CONFIG || !this.filterParams || Object.keys(this.filterParams).length === 0) {
      return;
    }

    this.isInitialized = true;
    this.init();
  }

  /**
   * Initialize component data and load dropdowns
   */
  init(): void {
    try {
      if (this.rowData && Object.keys(this.rowData).length > 0) {
        // Edit mode: Copy all rowData properties first (like AngularJS: vm.overrideEntity = rowData)
        this.isEdit = true;
        this.overrideEntity = safeJsonInput<any>(JSON.stringify(this.rowData), null, 'default-sourcing.parse1');
        
        // Map specific fields from rowData (like AngularJS)
        this.overrideEntity.FSI_KEY = this.rowData.DEF_FSI_KEY || '';
        this.overrideEntity.BASKET = this.rowData.OVERRIDE_DEFAULT_BASKET || '';
        
        // Ensure TAX_YEAR is set
        if (!this.overrideEntity.TAX_YEAR) {
          this.overrideEntity.TAX_YEAR = this.filterParams?.tax_year || this.globalService?.tax_year || '';
        }
      } else {
        // Add mode: Initialize with empty values
        this.isEdit = false;
        this.overrideEntity = {
          TAX_YEAR: this.filterParams?.tax_year || this.globalService?.tax_year || '',
          HO_LEID_RP: '',
          BASKET: '',
          FSI_KEY: ''
        };
      }

      // Create backup for reset functionality
      this.overrideEntity_backup = safeJsonInput<any>(JSON.stringify(this.overrideEntity), null, 'default-sourcing.parse2');

      if (this.USER_SETTINGS?.user?.sso_id) {
        this.logged_in_user = this.USER_SETTINGS.user.sso_id;
      }

      this.fetchDropdowns();
    } catch (e) {
      console.error('Error in component init:', e);
      this.showFormLoader = false;
      this.cdr.detectChanges();
    }
  }

  /**
   * Fetch dropdown options for HO_LEID_RP and BASKET
   * Matches exact payload structure for action codes u99vya and ms4tvf
   */
  fetchDropdowns(): void {
    if (this.isLoading) {
      return;
    }
    this.isLoading = true;
    this.showFormLoader = true;

    // Build params matching exact payload structure for action code u99vya
    const params1: any = {
      tax_year: this.filterParams?.tax_year || this.globalService?.tax_year || this.rowData?.TAX_YEAR || '',
      scenario: this.filterParams?.scenario || this.globalService?.scenario || this.rowData?.SCENARIO || '',
      jcd_key: this.filterParams?.jcd_key || this.globalService?.jcd_key || this.rowData?.JCD_KEY || '250',
      jcd_ta_key: this.filterParams?.jcd_ta_key || this.globalService?.jcd_ta_key || '',
      activity_key: this.filterParams?.activity_key || this.globalService?.activity_key || '',
      screen_key: this.filterParams?.screen_key || this.globalService?.screen_key || '',
      screen_type: this.filterParams?.screen_type || this.globalService?.screen_type || 'grid',
      is_locked: this.filterParams?.is_locked || this.globalService?.is_locked || 'N',
      issue_id: this.filterParams?.issue_id || this.globalService?.issue_id || '',
      issue_short_desc: this.filterParams?.issue_short_desc || this.globalService?.issue_short_desc || '',
      issue_id_for_tx_save: this.filterParams?.issue_id_for_tx_save || this.globalService?.issue_id_for_tx_save || '',
      scenarioCode: this.filterParams?.scenarioCode || this.filterParams?.scenario_code || this.globalService?.scenarioCode || this.globalService?.scenario_code || 'RAF',
      is_issue_key_enabled: 'N',
      sso_id: this.logged_in_user || this.USER_SETTINGS?.user?.sso_id || ''
    };

    const promise1 = this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject?action_code=u99vya',
      params1,
      this.globalService,
      null,
      null
    );

    // Build params matching exact payload structure for action code ms4tvf
    const params2: any = {
      tax_year: this.filterParams?.tax_year || this.globalService?.tax_year || this.rowData?.TAX_YEAR || '',
      scenario: this.filterParams?.scenario || this.globalService?.scenario || this.rowData?.SCENARIO || '',
      jcd_key: this.filterParams?.jcd_key || this.globalService?.jcd_key || this.rowData?.JCD_KEY || '250',
      jcd_ta_key: this.filterParams?.jcd_ta_key || this.globalService?.jcd_ta_key || '',
      activity_key: this.filterParams?.activity_key || this.globalService?.activity_key || '',
      screen_key: this.filterParams?.screen_key || this.globalService?.screen_key || '',
      screen_type: this.filterParams?.screen_type || this.globalService?.screen_type || 'grid',
      is_locked: this.filterParams?.is_locked || this.globalService?.is_locked || 'N',
      issue_id: this.filterParams?.issue_id || this.globalService?.issue_id || '',
      issue_short_desc: this.filterParams?.issue_short_desc || this.globalService?.issue_short_desc || '',
      issue_id_for_tx_save: this.filterParams?.issue_id_for_tx_save || this.globalService?.issue_id_for_tx_save || '',
      scenarioCode: this.filterParams?.scenarioCode || this.filterParams?.scenario_code || this.globalService?.scenarioCode || this.globalService?.scenario_code || 'RAF',
      is_issue_key_enabled: 'N',
      sso_id: this.logged_in_user || this.USER_SETTINGS?.user?.sso_id || ''
    };

    const promise2 = this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject?action_code=ms4tvf',
      params2,
      this.globalService,
      null,
      null
    );

    forkJoin([promise1, promise2]).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any[]) => {
        this.ho_leid_RP_options = response[0]?.jsonObject || [];
        this.basket_options = response[1]?.jsonObject || [];
        
        // In edit mode, ensure dropdown values are properly set after dropdowns load
        if (this.isEdit && this.rowData) {
          // Ensure HO_LEID_RP is set from rowData
          if (this.rowData.HO_LEID_RP && !this.overrideEntity.HO_LEID_RP) {
            this.overrideEntity.HO_LEID_RP = this.rowData.HO_LEID_RP;
          }
          
          // Ensure BASKET is set from rowData (OVERRIDE_DEFAULT_BASKET)
          if (this.rowData.OVERRIDE_DEFAULT_BASKET && !this.overrideEntity.BASKET) {
            this.overrideEntity.BASKET = this.rowData.OVERRIDE_DEFAULT_BASKET;
            // Update FSI_KEY when BASKET is set
            this.updateBasket();
          }
        }
        
        this.showFormLoader = false;
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading dropdowns:', error);
        this.gtwSnackbarService.openSnackBar(
          error?.message || 'Error occurred while loading dropdown options',
          'gtw-snackbar--red'
        );
        this.showFormLoader = false;
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    );
  }

  /**
   * Update FSI_KEY when BASKET selection changes
   * Matches AngularJS behavior: finds selected option and updates FSI_KEY
   */
  updateBasket(): void {
    if (!this.overrideEntity.BASKET || !this.basket_options || this.basket_options.length === 0) {
      return;
    }

    const selectedOption = this.basket_options.find((option: any) => {
      return option.BASKET === this.overrideEntity.BASKET;
    });

    if (selectedOption && selectedOption.FSI_KEY) {
      this.overrideEntity.FSI_KEY = selectedOption.FSI_KEY;
      this.cdr.detectChanges();
    }
  }

  /**
   * Reset form to original values
   * In edit mode: Restores original rowData values
   * In add mode: Clears all fields
   */
  reset(): void {
    if (this.isEdit) {
      // Edit mode: Restore to original rowData values
      this.overrideEntity = safeJsonInput<any>(JSON.stringify(this.overrideEntity_backup), null, 'default-sourcing.parse3');
    } else {
      // Add mode: Clear all fields except TAX_YEAR
      this.overrideEntity = {
        TAX_YEAR: this.filterParams?.tax_year || this.globalService?.tax_year || '',
        HO_LEID_RP: '',
        BASKET: '',
        FSI_KEY: ''
      };
      // Update backup after clearing
      this.overrideEntity_backup = safeJsonInput<any>(JSON.stringify(this.overrideEntity), null, 'default-sourcing.parse4');
    }
    this.clearValidationErrors();
    this.cdr.detectChanges();
  }

  /**
   * Clear validation error flags
   */
  private clearValidationErrors(): void {
    this.overrideEntity.TAX_YEAR_NOT_VALID = false;
    this.overrideEntity.HO_LEID_RP_NOT_VALID = false;
    this.overrideEntity.BASKET_NOT_VALID = false;
    this.overrideEntity.FSI_KEY_NOT_VALID = false;
  }

  /**
   * Validate form fields
   */
  isValid(): boolean {
    this.clearValidationErrors();
    let isValid = true;

    if (!this.overrideEntity.TAX_YEAR) {
      this.overrideEntity.TAX_YEAR_NOT_VALID = true;
      isValid = false;
    }

    if (!this.overrideEntity.HO_LEID_RP) {
      this.overrideEntity.HO_LEID_RP_NOT_VALID = true;
      isValid = false;
    }

    if (!this.overrideEntity.BASKET) {
      this.overrideEntity.BASKET_NOT_VALID = true;
      isValid = false;
    }

    if (!this.overrideEntity.FSI_KEY) {
      this.overrideEntity.FSI_KEY_NOT_VALID = true;
      isValid = false;
    }

    return isValid;
  }

  /**
   * Save form data
   */
  save(): void {
    if (!this.isValid()) {
      this.gtwSnackbarService.openSnackBar('Please fill all the required fields', 'gtw-snackbar--yellow');
      return;
    }

    if (this.crudLoading) {
      this.gtwSnackbarService.openSnackBar('Please wait while we save this request', 'gtw-snackbar--yellow');
      return;
    }

    this.isSaveClicked = true;
    this.crudLoading = true;
    // this.showFormLoader = true;
    this.cdr.detectChanges();

    this.overrideEntity.EDIT_FLAG = this.isEdit ? 'U' : 'I';

    // Build jsonSettings matching payload structure (tax_year, scenario, sso_id only)
    const jsonSettings = {
      tax_year: this.filterParams?.tax_year || this.globalService?.tax_year || this.rowData?.TAX_YEAR || '',
      scenario: this.filterParams?.scenario || this.globalService?.scenario || this.rowData?.SCENARIO || '',
      sso_id: this.logged_in_user || this.USER_SETTINGS?.user?.sso_id || ''
    };

    this.defaultSourcingService.saveDefaultSourcing(
      this.GENERAL_CONFIG.base_url,
      this.overrideEntity,
      jsonSettings,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;

        if (response.callSuccess === "1" && response.errorMessage === "null") {
          this.gtwSnackbarService.openSnackBar('Data has been updated successfully', 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        } else {
          this.gtwSnackbarService.openSnackBar(
            response.errorMessage || 'Error occurred while saving',
            'gtw-snackbar--red'
          );
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this.gtwSnackbarService.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    );
  }

  /**
   * Cancel and close modal
   */
  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}
